// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — the whole game, as a pure function of (mode, seed) plus a list of packed plies. No DOM,
// no clock, no network: that is what lets the bot, the balance sim, the mechanism audit and the
// lockstep replay all drive the SAME code, and it is why a desync is detectable rather than
// theoretical.
//
// THE BOARD. 5×5, cell = y*5 + x, y = 0 at the top. Seat 0 lives on the bottom row, seat 1 on the
// top. Each side's home-row centre is its COFFER; you win by walking your Keeper into THEIRS.
//
// THE SIGNATURE. Every move is paid for with a toll from your hand. The toll you pay goes onto the
// table, and you pick up one that was already there — so the tool you just used is the tool your
// opponent may use next. The whole game is deciding what you can afford to hand over.
//
// THE OPENING IS A PIE RULE, NOT A DEAL. Seat 0 splits the dealt tolls into two pairs (the rest go
// on the table); seat 1 picks which pair it wants; seat 0 then moves first. That is deliberate and
// load-bearing: a plain deal in this shape hands one seat a lopsided opening and no amount of
// tuning fixes it, whereas split-and-choose makes the splitter price their own first-move edge.
// tests/balance.test.ts holds a COUNTER-CONTROL arm with the pie rule removed, and it must skew
// worse — if it does not, this rule is decoration and should be cut rather than admired.
//
// TERMINATION IS NOT FREE HERE. Skyhold terminated because total height only ever rose; Tithe has
// no such potential — captures are the only irreversible event and nothing forces one. So the third
// ending is IMPASSE: create a position for the third time and you lose. It almost never fires, and
// that is the point; it exists so that "shuffle forever" is not a strategy and so the search has a
// gradient in a dead position.

import { makeRng } from '@ben-gy/game-engine/rng';
import { POOLS, TOLLS, isTollId, tollOf } from './tolls';
import { modeOf, type Mode } from './modes';

/** Bumped on ANY change to the meaning of a ply or the rules. A mismatch across the wire is a
 *  stale cached build, which is a different problem from a desync and is reported as such. */
export const RULES_REV = 1;

export const W = 5;
export const H = 5;
export const NC = W * H;
/** Pieces per side: one Keeper (local index 0) and four Bearers. */
export const PPS = 5;
export const NONE = 31;

/**
 * The hard ceiling on a round, DERIVED rather than picked: the levy removes at least one Bearer
 * every `mode.levy` turns while any are standing, so after 2×(PPS−1) windows there are none left
 * and the next one ends the round outright. tests/rules.test.ts re-derives this and the balance sim
 * asserts no game ever gets near it.
 */
export const maxTurnsFor = (m: Mode): number => m.levy * (2 * (PPS - 1) + 1);

export const PH_SPLIT = 0;
export const PH_CHOOSE = 1;
export const PH_PLAY = 2;
export const PH_OVER = 3;

export const K_MOVE = 0;
export const K_SLACK = 1;
export const K_SPLIT = 2;
export const K_CHOOSE = 3;

/** Seat 0's coffer is on the bottom row, seat 1's on the top. You win by reaching THEIRS. */
export const COFFER = [22, 2] as const;

/**
 * Which seat moves first once the split is settled. Seat 0 CUTS the tolls into two pairs and seat 1
 * CHOOSES one — so handing seat 0 the first move as well would pay the cutter twice, and the
 * simulation says so out loud. This constant is a number the sweep chose, not a preference; see
 * tests/balance.test.ts, which pins it and fails if it is flipped.
 */
export const FIRST_MOVER = 1;

export type WinReason = '' | 'seize' | 'collect' | 'drained' | 'walkover' | 'nocontest';

export interface PlyEvent {
  kind: 'move' | 'take' | 'seize' | 'collect' | 'drained' | 'levy' | 'slack' | 'pay' | 'split' | 'choose';
  seat?: number;
  from?: number;
  to?: number;
  toll?: number;
  /** Local piece index of the piece that was taken. */
  victim?: number;
  keeper?: boolean;
}

export interface SeatStats {
  turns: number;
  /** Bearers taken from the opponent. */
  takes: number;
  /** Bearers lost. */
  lost: number;
  /** Best (smallest) rank distance this Keeper ever reached from the enemy coffer. */
  closest: number;
  /** Turns on which this seat's move left the enemy Keeper attacked by a toll it holds. */
  threats: number;
  /** Bearers this seat forfeited to the levy. */
  levied: number;
  /** Turns where no piece could move at all and only a toll changed hands. */
  slack: number;
  /** Sum of legal-ply counts, for a mean-mobility readout. */
  mobSum: number;
  mobN: number;
}

export interface State {
  mode: Mode;
  seed: number;
  phase: number;
  turn: number;
  winner: number;
  reason: WinReason;
  ply: number;
  /** Committed play turns (split/choose excluded). */
  turns: number;
  /** cell -> piece index, or -1. */
  occ: Int8Array;
  /** piece index -> cell, or -1 when taken. */
  pos: Int8Array;
  /** hand[seat*2 + i] = toll id. Kept sorted so the digest is canonical. */
  hand: Int8Array;
  /** The tolls on the table, sorted ascending. Length is mode.table. */
  table: number[];
  /** The tolls dealt at the start, in deal order — the split indexes into this. */
  deal: number[];
  /** The two pairs seat 0 offered, once the split is committed. */
  offer: [number[], number[]] | null;
  log: number[];
  /** Turns since a piece was last taken. At `mode.levy` the tide collects — see applyLevy. */
  levy: number;
  /** Set for one paint after a levy, so the board can show what it took. */
  levied: number[];
  stats: [SeatStats, SeatStats];
  /** Set on the ply that produced a win, for the board flourish. */
  winCell: number;
  /**
   * True only inside the bot's search. It skips the log, the stats and the repetition table —
   * all of which are per-GAME bookkeeping that a hypothetical line has no business writing into.
   * The search tracks repetition itself along its own path (see bot.ts), which is both cheaper and
   * more correct: a line that repeats inside the search is a draw to be avoided, not a loss to be
   * recorded. Everything that decides legality or a win stays identical, which is the whole point
   * of having one rules implementation.
   */
  searchMode: boolean;
  /**
   * SEARCH ONLY, and only for the balance sim's control arm: when true the toll exchange is
   * skipped, so a bot planning with this flag believes its hand never changes and that nothing it
   * pays ever reaches the opponent. That is the honest way to be "blind to the signature mechanic"
   * — far better than zeroing a decorative eval weight, because the rotation is not something the
   * evaluation knows about in the first place, it is something the SEARCH lives through. The
   * measurement that matters is whether the sighted bot beats this one; if it does not, the
   * handover is decoration. Never set on a real game: `step` is otherwise identical.
   */
  frozenTolls: boolean;
}

// ── ply packing ─────────────────────────────────────────────────────────────────
// from(5) | to(5) | hand(1) | take(1) | kind(2) | idx(7)  — 21 bits, one uint32.
export const packMove = (from: number, to: number, hand: number, take: number): number =>
  ((from & 31) | ((to & 31) << 5) | ((hand & 1) << 10) | ((take & 1) << 11) | (K_MOVE << 12)) >>> 0;
export const packSlack = (hand: number, take: number): number =>
  ((NONE | (NONE << 5) | ((hand & 1) << 10) | ((take & 1) << 11) | (K_SLACK << 12)) >>> 0);
export const packSplit = (idx: number): number => ((NONE | (NONE << 5) | (K_SPLIT << 12) | ((idx & 127) << 14)) >>> 0);
export const packChoose = (which: number): number => ((NONE | (NONE << 5) | (K_CHOOSE << 12) | ((which & 127) << 14)) >>> 0);

export const plyFrom = (p: number): number => p & 31;
export const plyTo = (p: number): number => (p >>> 5) & 31;
export const plyHand = (p: number): number => (p >>> 10) & 1;
export const plyTake = (p: number): number => (p >>> 11) & 1;
export const plyKind = (p: number): number => (p >>> 12) & 3;
export const plyIdx = (p: number): number => (p >>> 14) & 127;

// ── geometry ────────────────────────────────────────────────────────────────────
export const cx = (c: number): number => c % W;
export const cy = (c: number): number => (c / W) | 0;
export const seatOfPiece = (p: number): number => (p < PPS ? 0 : 1);
export const isKeeper = (p: number): boolean => p % PPS === 0;
export const keeperOf = (s: number): number => s * PPS;

const FILES = 'abcde';
export const cellName = (c: number): string => (c < 0 || c >= NC ? '—' : `${FILES[cx(c)]}${H - cy(c)}`);

/**
 * The printed card is read from the mover's own side, so seat 1 reads the same card rotated 180°.
 * One line, and it is the only place handedness is decided.
 */
export const orient = (seat: number, dx: number, dy: number): [number, number] =>
  // `+ 0` normalises away negative zero. It is arithmetically irrelevant and it makes every
  // downstream comparison — tests, audits, anything using Object.is — behave.
  seat === 0 ? [dx + 0, -dy + 0] : [-dx + 0, dy + 0];

/** How many ranks this seat's Keeper still has to travel to reach the enemy coffer's row. */
export const ranksToCoffer = (seat: number, cell: number): number =>
  seat === 0 ? cy(cell) : H - 1 - cy(cell);

// ── setup ───────────────────────────────────────────────────────────────────────
function homeCells(seat: number, setup: 'flat' | 'forward'): number[] {
  const row = seat === 0 ? H - 1 : 0;
  const fwd = seat === 0 ? row - 1 : row + 1;
  const at = (x: number, y: number): number => y * W + x;
  // Local index 0 is always the Keeper, on the coffer.
  if (setup === 'flat') return [at(2, row), at(0, row), at(1, row), at(3, row), at(4, row)];
  return [at(2, row), at(0, row), at(4, row), at(1, fwd), at(3, fwd)];
}

/**
 * Every unordered {pair, pair} split of `k` dealt tolls, as index pairs. Canonicalised by
 * requiring the first pair to hold the smaller opening index, so each split appears exactly once:
 * 15 for a five-toll deal, 45 for six.
 */
export function partitions(k: number): Array<[number, number, number, number]> {
  const out: Array<[number, number, number, number]> = [];
  for (let i = 0; i < k; i++) {
    for (let j = i + 1; j < k; j++) {
      for (let a = 0; a < k; a++) {
        if (a === i || a === j || a < i) continue;
        for (let b = a + 1; b < k; b++) {
          if (b === i || b === j) continue;
          out.push([i, j, a, b]);
        }
      }
    }
  }
  return out;
}

export function createState(modeId: string, seed: number): State {
  const mode = modeOf(modeId);
  const rnd = makeRng(seed >>> 0);
  const bag = POOLS[mode.pool].slice();
  // Fisher–Yates from the shared seed — identical on every peer, which is the whole determinism
  // contract for a lockstep game.
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    const t = bag[i];
    bag[i] = bag[j];
    bag[j] = t;
  }
  const deal = bag.slice(0, mode.deal);

  const occ = new Int8Array(NC).fill(-1);
  const pos = new Int8Array(PPS * 2).fill(-1);
  for (const seat of [0, 1]) {
    const cells = homeCells(seat, mode.setup);
    cells.forEach((c, k) => {
      const p = seat * PPS + k;
      pos[p] = c;
      occ[c] = p;
    });
  }

  const blank = (): SeatStats => ({ turns: 0, takes: 0, lost: 0, closest: H - 1, threats: 0, levied: 0, slack: 0, mobSum: 0, mobN: 0 });

  return {
    mode,
    seed: seed >>> 0,
    phase: PH_SPLIT,
    turn: 0,
    winner: -1,
    reason: '',
    ply: 0,
    turns: 0,
    occ,
    pos,
    hand: new Int8Array(4).fill(-1),
    table: [],
    deal,
    offer: null,
    log: [],
    levy: 0,
    levied: [],
    stats: [blank(), blank()],
    winCell: -1,
    searchMode: false,
    frozenTolls: false,
  };
}

/** A detached copy. The search preallocates a pool of these and copies into them. */
export function cloneState(s: State): State {
  return {
    ...s,
    occ: s.occ.slice(),
    pos: s.pos.slice(),
    hand: s.hand.slice(),
    table: s.table.slice(),
    deal: s.deal.slice(),
    offer: s.offer ? [s.offer[0].slice(), s.offer[1].slice()] : null,
    log: s.log.slice(),
    levied: s.levied.slice(),
    stats: [{ ...s.stats[0] }, { ...s.stats[1] }],
  };
}

/** Overwrite `dst` with `src` without allocating. Used once per search node. */
export function copyInto(dst: State, src: State): void {
  dst.occ.set(src.occ);
  dst.pos.set(src.pos);
  dst.hand.set(src.hand);
  dst.table.length = 0;
  for (const t of src.table) dst.table.push(t);
  dst.mode = src.mode;
  dst.seed = src.seed;
  dst.phase = src.phase;
  dst.turn = src.turn;
  dst.winner = src.winner;
  dst.reason = src.reason;
  dst.ply = src.ply;
  dst.turns = src.turns;
  dst.levy = src.levy;
  dst.levied.length = 0;
  dst.winCell = src.winCell;
  dst.deal = src.deal;
  dst.offer = src.offer;
  dst.log.length = 0;
  dst.searchMode = true;
  dst.frozenTolls = src.frozenTolls;
}

export const isOver = (s: State): boolean => s.phase === PH_OVER;

// ── move generation ─────────────────────────────────────────────────────────────
/** Destination cells this seat could reach with `toll`, from `cell`. Bounds- and friend-checked. */
function stepsFrom(s: State, seat: number, cell: number, tollId: number, out: number[]): void {
  const t = tollOf(tollId);
  const x0 = cx(cell);
  const y0 = cy(cell);
  for (const [dx, dy] of t.off) {
    const [sx, sy] = orient(seat, dx, dy);
    const x = x0 + sx;
    const y = y0 + sy;
    if (x < 0 || x >= W || y < 0 || y >= H) continue;
    const to = y * W + x;
    const there = s.occ[to];
    if (there >= 0 && seatOfPiece(there) === seat) continue;
    out.push(to);
  }
}

/** How many table slots a mover may choose between. One in Ledger and Spree, two in Arrears. */
const takeSlots = (s: State): number => Math.max(1, s.table.length);

export function genPlies(s: State): number[] {
  if (s.phase === PH_OVER) return [];
  if (s.phase === PH_SPLIT) return partitions(s.deal.length).map((_, i) => packSplit(i));
  if (s.phase === PH_CHOOSE) return [packChoose(0), packChoose(1)];

  const out: number[] = [];
  const slots = takeSlots(s);
  const dests: number[] = [];
  for (let h = 0; h < 2; h++) {
    const toll = s.hand[s.turn * 2 + h];
    if (!isTollId(toll)) continue;
    for (let k = 0; k < PPS; k++) {
      const p = s.turn * PPS + k;
      const from = s.pos[p];
      if (from < 0) continue;
      dests.length = 0;
      stepsFrom(s, s.turn, from, toll, dests);
      for (const to of dests) {
        for (let take = 0; take < slots; take++) out.push(packMove(from, to, h, take));
      }
    }
  }
  if (out.length > 0) return out;
  // A side with nothing to move still pays a toll — which of the two is a real decision, and it is
  // also what makes a stalemate impossible without inventing a rule for one.
  for (let h = 0; h < 2; h++) for (let take = 0; take < slots; take++) out.push(packSlack(h, take));
  return out;
}

/** True if `seat` could reach `cell` next turn with a toll it holds RIGHT NOW. */
export function attacked(s: State, seat: number, cell: number, alsoTable = false): boolean {
  const dests: number[] = [];
  const tolls: number[] = [s.hand[seat * 2], s.hand[seat * 2 + 1]];
  if (alsoTable) tolls.push(...s.table);
  for (const toll of tolls) {
    if (!isTollId(toll)) continue;
    for (let k = 0; k < PPS; k++) {
      const p = seat * PPS + k;
      const from = s.pos[p];
      if (from < 0) continue;
      dests.length = 0;
      stepsFrom(s, seat, from, toll, dests);
      if (dests.includes(cell)) return true;
    }
  }
  return false;
}

// ── repetition ──────────────────────────────────────────────────────────────────
/**
 * The position, for the bot's transposition table. Occupancy, both hands, the table, the side to
 * move — and THE LEVY COUNTER, which is what makes repetition a non-concept here: two positions
 * that look identical but sit at different points in the levy window are genuinely different, one
 * of them being closer to losing a piece. That is why there is no draw-by-repetition rule and no
 * repetition path array in the search; the levy already supplies the pressure both would exist to
 * create, and it does it as a real rule the player can see and play for.
 *
 * Two independent FNV-1a passes combined into one 48-bit number, so a transposition collision is
 * far rarer than any other way the two peers could disagree. Deterministic and endian-free, so both
 * peers compute the identical number.
 */
export function positionHash(s: State): number {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  const mix = (v: number): void => {
    a = Math.imul(a ^ (v & 0xff), 0x01000193) >>> 0;
    b = Math.imul(b ^ (v & 0xff), 0x85ebca6b) >>> 0;
    b = (b ^ (b >>> 13)) >>> 0;
  };
  for (let c = 0; c < NC; c++) mix(s.occ[c] + 2);
  for (let i = 0; i < 4; i++) mix(s.hand[i] + 2);
  for (const t of s.table) mix(t + 2);
  mix(s.table.length + 40);
  mix(s.turn + 60);
  mix(s.levy + 80);
  return a * 65536 + (b & 0xffff);
}

// ── stepping ────────────────────────────────────────────────────────────────────
function finish(s: State, winner: number, reason: WinReason, cell: number, events: PlyEvent[]): void {
  s.phase = PH_OVER;
  s.winner = winner;
  s.reason = reason;
  s.winCell = cell;
  events.push({
    kind: reason === 'collect' ? 'collect' : reason === 'drained' ? 'drained' : 'seize',
    seat: winner,
    to: cell,
  });
}

/** Move the toll at hand slot `h` onto the table and pick up table slot `take`. */
function exchange(s: State, h: number, take: number, events: PlyEvent[]): void {
  const seat = s.turn;
  const paid = s.hand[seat * 2 + h];
  if (s.frozenTolls) {
    events.push({ kind: 'pay', seat, toll: paid });
    return;
  }
  const idx = Math.min(Math.max(take, 0), Math.max(0, s.table.length - 1));
  const got = s.table.length > 0 ? s.table[idx] : paid;
  if (s.table.length > 0) s.table.splice(idx, 1);
  s.table.push(paid);
  s.table.sort((a, b) => a - b);
  s.hand[seat * 2 + h] = got;
  // Hands stay sorted so the digest and the position key are canonical on every peer.
  const a = s.hand[seat * 2];
  const b = s.hand[seat * 2 + 1];
  s.hand[seat * 2] = Math.min(a, b);
  s.hand[seat * 2 + 1] = Math.max(a, b);
  events.push({ kind: 'pay', seat, toll: paid });
}

export function step(s: State, ply: number, events: PlyEvent[] = []): void {
  if (s.phase === PH_OVER) return;
  const kind = plyKind(ply);
  if (!s.searchMode) s.log.push(ply >>> 0);
  s.ply++;
  s.levied = [];

  if (kind === K_SPLIT) {
    const all = partitions(s.deal.length);
    const part = all[Math.min(plyIdx(ply), all.length - 1)];
    const [i, j, a, b] = part;
    const pairA = [s.deal[i], s.deal[j]].sort((x, y) => x - y);
    const pairB = [s.deal[a], s.deal[b]].sort((x, y) => x - y);
    const used = new Set([i, j, a, b]);
    s.table = s.deal.filter((_, k) => !used.has(k)).sort((x, y) => x - y);
    s.offer = [pairA, pairB];
    s.phase = PH_CHOOSE;
    s.turn = 1;
    events.push({ kind: 'split', seat: 0 });
    return;
  }

  if (kind === K_CHOOSE) {
    const which = plyIdx(ply) & 1;
    const offer = s.offer ?? [[s.deal[0], s.deal[1]], [s.deal[2], s.deal[3]]];
    const mine = offer[which];
    const theirs = offer[1 - which];
    s.hand[2] = mine[0];
    s.hand[3] = mine[1];
    s.hand[0] = theirs[0];
    s.hand[1] = theirs[1];
    s.phase = PH_PLAY;
    s.turn = FIRST_MOVER;
    events.push({ kind: 'choose', seat: 1 });
    return;
  }

  const seat = s.turn;
  const st = s.stats[seat];
  if (!s.searchMode) {
    st.turns++;
    const legal = genPlies(s);
    st.mobSum += legal.length;
    st.mobN++;
  }

  let took = false;
  if (kind === K_SLACK) {
    if (!s.searchMode) st.slack++;
    events.push({ kind: 'slack', seat });
    exchange(s, plyHand(ply), plyTake(ply), events);
  } else {
    const from = plyFrom(ply);
    const to = plyTo(ply);
    const mover = s.occ[from];
    const victim = s.occ[to];
    const tollId = s.hand[seat * 2 + plyHand(ply)];

    if (victim >= 0) {
      took = true;
      s.pos[victim] = -1;
      if (!s.searchMode && !isKeeper(victim)) {
        s.stats[seat].takes++;
        s.stats[1 - seat].lost++;
      }
      events.push({ kind: 'take', seat, to, victim: victim % PPS, keeper: isKeeper(victim) });
    }
    s.occ[from] = -1;
    s.occ[to] = mover;
    s.pos[mover] = to;
    events.push({ kind: 'move', seat, from, to, toll: tollId });

    if (!s.searchMode && isKeeper(mover)) {
      const d = ranksToCoffer(seat, to);
      if (d < st.closest) st.closest = d;
    }

    // Pay the toll BEFORE testing for a win, so the results screen shows the hand the winning
    // position actually left behind rather than the one that produced it.
    exchange(s, plyHand(ply), plyTake(ply), events);

    if (victim >= 0 && isKeeper(victim)) {
      finish(s, seat, 'seize', to, events);
      return;
    }
    if (isKeeper(mover) && to === COFFER[1 - seat]) {
      finish(s, seat, 'collect', to, events);
      return;
    }
    if (!s.searchMode && attacked(s, seat, s.pos[keeperOf(1 - seat)])) st.threats++;
  }

  s.turns++;
  s.turn = 1 - seat;

  // PRECEDENCE, and it is deliberate. A win is checked and returned above, before anything else, so
  // a taking move that also wins can never be undone by the levy. The levy is checked AFTER the
  // turn passes, so the counter a player sees on their turn is the one that applies to them.
  if (took) s.levy = 0;
  else s.levy++;
  if (s.levy >= s.mode.levy) applyLevy(s, events);
}

export const bearersLeft = (s: State, seat: number): number => {
  let n = 0;
  for (let k = 1; k < PPS; k++) if (s.pos[seat * PPS + k] >= 0) n++;
  return n;
};

/**
 * THE LEVY. Nothing has been taken for a whole window, so the tide takes something: whoever has
 * more pieces standing forfeits their REARMOST Bearer, and if the two sides are level they both do.
 *
 * Three properties, all of which are the reason it exists rather than a turn cap:
 *  - it takes from whoever is AHEAD, so sitting on a lead is the one thing it punishes;
 *  - it is symmetric, so there is no seat that can farm it;
 *  - the total number of pieces strictly falls every window, which is the monotone quantity this
 *    game otherwise does not have and the reason `maxTurnsFor` is a derivation, not a guess.
 * When there is nothing left to take, the round ends and the Keeper nearer the enemy coffer wins.
 */
function applyLevy(s: State, events: PlyEvent[]): void {
  s.levy = 0;
  s.levied = [];
  const n0 = bearersLeft(s, 0);
  const n1 = bearersLeft(s, 1);
  const victims = n0 > n1 ? [0] : n1 > n0 ? [1] : [0, 1];
  let took = false;
  for (const seat of victims) {
    // "Rearmost" in that seat's own frame: seat 0 sits on the bottom row, so its rear is the
    // HIGHEST cell index; seat 1's rear is the lowest. A total order, so there is never a tie.
    let worst = -1;
    let worstCell = -1;
    for (let k = 1; k < PPS; k++) {
      const p = seat * PPS + k;
      const at = s.pos[p];
      if (at < 0) continue;
      if (worst < 0 || (seat === 0 ? at > worstCell : at < worstCell)) {
        worst = p;
        worstCell = at;
      }
    }
    if (worst < 0) continue;
    took = true;
    s.pos[worst] = -1;
    s.occ[worstCell] = -1;
    s.levied.push(worstCell);
    if (!s.searchMode) s.stats[seat].levied++;
    events.push({ kind: 'levy', seat, to: worstCell });
  }
  if (took) return;

  // Both sides are down to a lone Keeper and neither has closed. The tide decides: whoever walked
  // further wins, and a dead-level position goes to the seat that moved second, which is the one
  // concession to arbitration in the whole rule set.
  const d0 = ranksToCoffer(0, s.pos[keeperOf(0)]);
  const d1 = ranksToCoffer(1, s.pos[keeperOf(1)]);
  const win = d0 !== d1 ? (d0 < d1 ? 0 : 1) : 1 - FIRST_MOVER;
  finish(s, win, 'drained', s.pos[keeperOf(win)], events);
}

/** Replay a log onto a fresh state — the lockstep receiver, the analysis pass and the sim all use it. */
export function replay(modeId: string, seed: number, log: readonly number[]): State {
  const s = createState(modeId, seed);
  for (const p of log) {
    if (isOver(s)) break;
    step(s, p >>> 0);
  }
  return s;
}

// ── digest ──────────────────────────────────────────────────────────────────────
/** FNV-1a over everything two peers must agree about. Cheap, and it catches a divergence on the
 *  ply it happens rather than three moves later when the board looks wrong. */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (v >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  for (let c = 0; c < NC; c++) mix(s.occ[c] + 2);
  for (let i = 0; i < 4; i++) mix(s.hand[i] + 2);
  for (const t of s.table) mix(t + 2);
  mix(s.table.length);
  mix(s.turn);
  mix(s.phase);
  mix(s.winner + 2);
  mix(s.turns);
  mix(s.ply);
  mix(s.levy);
  // The mode goes in too: two peers on different modes must fail as a MISMATCH on the first
  // message rather than diverging quietly three moves later on a board that merely looks odd.
  for (let i = 0; i < s.mode.id.length; i++) mix(s.mode.id.charCodeAt(i));
  return h >>> 0;
}

/** Human-readable move text for the move list and the post-game analysis. */
export function plyText(s: State, ply: number): string {
  const kind = plyKind(ply);
  if (kind === K_SPLIT) return 'splits the tolls';
  if (kind === K_CHOOSE) return 'takes a pair';
  const toll = s.hand[s.turn * 2 + plyHand(ply)];
  const name = isTollId(toll) ? tollOf(toll).name : '—';
  if (kind === K_SLACK) return `pays ${name}, nothing can move`;
  return `${cellName(plyFrom(ply))}→${cellName(plyTo(ply))} · ${name}`;
}

export const TOLL_COUNT = TOLLS.length;
